import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import LeadDrawer from "@/components/LeadDrawer";
import { StatusBadge } from "@/components/StatusBadge";
import { Truck, Clock, CheckCircle2, PhoneOff, CalendarClock, PackageCheck, FileText, MessageCircle, MapPin, Star, Briefcase, Coins, Wallet, CalendarCheck, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";

const CARDS = [
  { key: "total", label: "Total Leads", icon: Truck, tint: "text-foreground" },
  { key: "pending", label: "Pending", icon: Clock, tint: "text-amber-500" },
  { key: "quote_required", label: "Quote Required", icon: FileText, tint: "text-blue-500" },
  { key: "followup", label: "Follow-ups", icon: CalendarClock, tint: "text-purple-500" },
  { key: "not_picked", label: "Not Picked", icon: PhoneOff, tint: "text-red-500" },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle2, tint: "text-emerald-500" },
  { key: "dispatched", label: "Dispatched", icon: PackageCheck, tint: "text-primary" },
];

function TodaysPickups() {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = () => api.get("/leads/today").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const orders = data.orders || [];
  const waHref = `https://wa.me/?text=${encodeURIComponent(data.digest || "")}`;

  return (
    <div className="mt-8" data-testid="todays-pickups">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-emerald-500" />
          <h2 className="font-heading font-bold text-2xl uppercase tracking-tight">Today's Pickups</h2>
          <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-2 bg-emerald-500 text-white text-xs font-bold" data-testid="todays-count">{data.count}</span>
        </div>
        <a
          href={waHref}
          target="_blank"
          rel="noreferrer"
          className={`inline-flex items-center gap-2 h-10 px-4 font-bold uppercase tracking-wide text-sm transition-colors ${orders.length ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-muted text-muted-foreground pointer-events-none"}`}
          data-testid="send-digest-btn"
        >
          <MessageCircle className="h-4 w-4" /> Send Digest to Ops
        </a>
      </div>
      <div className="border border-border bg-card divide-y divide-border">
        {orders.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No shipments scheduled for pickup today.</div>}
        {orders.map((o) => (
          <button key={o.id} onClick={() => setOpenId(o.id)} className="w-full text-left flex items-center gap-4 p-3 hover:bg-muted transition-colors" data-testid={`today-row-${o.order_number}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-num font-bold text-primary">{o.order_number}</span>
                {o.lr_number && <span className="font-mono-num text-xs border border-border px-1.5 py-0.5 text-muted-foreground">LR {o.lr_number}</span>}
              </div>
              <div className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" />{o.customer_name} · {o.route}</div>
              {o.remarks && <div className="text-xs text-muted-foreground mt-0.5 truncate flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{o.remarks}</span></div>}
            </div>
            <StatusBadge status={o.order_state === "confirmed_dispatched" ? "confirmed_dispatched" : o.status} />
          </button>
        ))}
      </div>
      <LeadDrawer leadId={openId} onClose={() => setOpenId(null)} onChange={load} />
    </div>
  );
}

function OverheadSummary() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/office/overhead").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const cards = [
    { label: "Fixed Costs", value: d.fixed_total, icon: Wallet, tint: "text-foreground" },
    { label: "Cash Paid", value: d.cash_paid, icon: Coins, tint: "text-amber-500" },
    { label: "Total Overhead", value: d.total_overhead, icon: Briefcase, tint: "text-red-500" },
    { label: "Booking Profit", value: d.booking_profit, icon: TrendingUp, tint: "text-emerald-500" },
    { label: "Net Margin", value: d.net_margin, icon: (d.net_margin || 0) >= 0 ? TrendingUp : TrendingDown, tint: (d.net_margin || 0) >= 0 ? "text-emerald-500" : "text-red-500" },
    { label: "Attendance", value: `${d.present}P / ${d.absent}A`, icon: CalendarCheck, tint: "text-blue-500" },
  ];
  const money = (n) => (typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : n);
  return (
    <div className="mt-8" data-testid="dashboard-overhead">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" />
          <h2 className="font-heading font-bold text-2xl uppercase tracking-tight">Monthly Overhead</h2>
          <span className="text-xs text-muted-foreground font-mono-num">{d.month}</span>
        </div>
        <Link to="/dashboard/office" className="text-sm text-primary hover:underline font-bold uppercase tracking-wide" data-testid="overhead-manage-link">Manage Office →</Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border border border-border">
        {cards.map((c) => (
          <div key={c.label} className="bg-card p-5" data-testid={`overhead-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">{c.label}</span><c.icon className={`h-4 w-4 ${c.tint}`} /></div>
            <div className={`font-mono-num font-bold text-2xl mt-3 ${c.tint}`}>{money(c.value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const isManager = user?.role === "admin" || user?.role === "manager";

  useEffect(() => { api.get("/leads/stats").then((r) => setStats(r.data)); }, []);

  const quickLinks = [
    { to: "/dashboard/leads", icon: Truck, title: isManager ? "Lead Management" : "My Leads", desc: isManager ? "Assign, distribute and drive leads to quotes." : "Log calls, update status & schedule follow-ups.", roles: ["admin", "manager", "calling_executive"] },
    { to: "/dashboard/vendors", icon: PackageCheck, title: "Vendor Network", desc: "Filter partners by state & city, send anonymized RFQs.", roles: ["admin", "manager", "calling_executive"] },
    { to: "/dashboard/tracking", icon: MapPin, title: "Live Driver Tracking", desc: "See all active driver locations on a live map.", roles: ["admin", "manager"] },
    { to: "/dashboard/quotes", icon: MessageCircle, title: "Recent Vendor Quotes", desc: "All incoming vendor bids for active leads.", roles: ["admin", "manager"] },
    { to: "/dashboard/feedback", icon: Star, title: "Partner Feedback", desc: "Customer reviews and average partner ratings.", roles: ["admin", "manager"] },
  ].filter((l) => l.roles.includes(user?.role));

  return (
    <div>
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-widest text-primary">Overview</div>
        <h1 className="font-heading font-black text-4xl tracking-tight">Hello, {user?.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground text-sm">{user?.role === "calling_executive" ? "Your assigned pipeline at a glance." : "Operational pipeline across the network."}</p>
      </div>

      {stats?.open_claims > 0 && (
        <Link to="/dashboard/ilot" className="mb-6 flex items-center gap-3 border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 px-4 py-3 hover:bg-red-500/20 transition-colors" data-testid="open-claims-badge">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span className="font-bold text-sm">{stats.open_claims} open damage claim{stats.open_claims === 1 ? "" : "s"}</span>
          <span className="text-xs opacity-70 hidden sm:inline">— needs attention so nothing slips through</span>
          <span className="ml-auto text-xs font-bold uppercase tracking-wide">Review →</span>
        </Link>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
        {CARDS.map((c, i) => (
          <motion.div key={c.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className="bg-card p-5" data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">{c.label}</span>
              <c.icon className={`h-4 w-4 ${c.tint}`} />
            </div>
            <div className="font-mono-num font-bold text-3xl mt-3">{stats ? stats[c.key] ?? 0 : "—"}</div>
          </motion.div>
        ))}
        {isManager && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-card p-5" data-testid="stat-unassigned">
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Unassigned</span><Truck className="h-4 w-4 text-amber-500" /></div>
            <div className="font-mono-num font-bold text-3xl mt-3">{stats ? stats.unassigned : "—"}</div>
          </motion.div>
        )}
      </div>

      <TodaysPickups />

      {isManager && <OverheadSummary />}

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {quickLinks.map((l) => (
          <Link key={l.to} to={l.to} className="group border border-border bg-card p-6 hover:border-primary transition-colors" data-testid={`quick-${l.to.split("/").pop()}`}>
            <l.icon className="h-6 w-6 text-primary mb-3" />
            <div className="font-heading font-bold text-xl uppercase tracking-tight">{l.title}</div>
            <p className="text-sm text-muted-foreground mt-1">{l.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
